import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, MapPin, Calendar, FileQuestion } from "lucide-react";
import { api } from "@/lib/api";
import { DOC_TYPES } from "@/lib/docTypes";
import DatePicker from "@/components/DatePicker";

const formatExplorerDate = (value) => value ? new Date(value).toLocaleString("id-ID") : "-";

export default function Archive() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [categoryTypes, setCategoryTypes] = useState(DOC_TYPES);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50, page };
      if (q) params.q = q;
      if (type !== "all") params.document_type = type;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/documents", { params });

      if (Array.isArray(data)) {
        setDocs(data);
        setTotal(data.length);
        setPages(1);
      } else {
        setDocs(data.documents || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      }
    } finally {
      setLoading(false);
    }
  }, [q, type, dateFrom, dateTo, page]);

  useEffect(() => {
    api.get("/documents/types")
      .then(({ data }) => {
        const values = data.types || [];
        setCategoryTypes(values.length ? values : DOC_TYPES);
      })
      .catch(() => setCategoryTypes(DOC_TYPES));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [q, type, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(fetchDocs, 250);
    return () => clearTimeout(t);
  }, [fetchDocs]);

  const openDetail = (doc) => navigate(`/arsip/${doc.slug || doc._id}`);
  const inputCls = "w-full border c-input rounded-lg text-sm outline-none focus:border-gold focus:ring-1 focus:ring-gold transition";

  return (
    <div className="h-screen overflow-hidden p-4 pt-16 sm:p-8 lg:p-12 max-w-6xl mx-auto animate-fade-up flex flex-col">
      <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2">Arsip Dokumen</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight c-strong mb-1">Telusuri Arsip</h1>
      <p className="text-sm c-muted mb-8">Cari dokumen apa pun dalam hitungan detik.</p>

      <div className="c-card border rounded-xl p-3 sm:p-4 mb-5 card-elev space-y-3 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-3">
        <div className="sm:col-span-8 relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A0A0A0] z-10" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" placeholder="Cari nama klien, nomor akta..." className={`${inputCls} pl-10 pr-3 py-2.5`} />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:col-span-4 sm:grid-cols-2 sm:gap-3">
          <DatePicker value={dateFrom} onChange={setDateFrom} testid="filter-date-from" placeholder="Dari tgl" />
          <DatePicker value={dateTo} onChange={setDateTo} testid="filter-date-to" placeholder="Sampai tgl" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="filter-type-buttons">
        {[{ label: "Semua Jenis", value: "all" }, ...categoryTypes.map((t) => ({ label: t, value: t }))].map((item) => (
          <button
            key={item.value}
            onClick={() => { setType(item.value); setPage(1); }}
            data-testid={`filter-type-${item.value}`}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border ${
              type === item.value
                ? "bg-gold text-white border-gold shadow-md shadow-gold/20"
                : "c-soft c-muted border c-border hover:border-gold/40 hover:text-gold"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="text-xs c-muted mb-3 flex items-center justify-between gap-3" data-testid="result-count">
        <span><span className="font-mono text-gold">{total}</span> dokumen ditemukan</span>
        <span className="font-mono">Halaman {page}/{pages}</span>
      </div>

      <div className="hidden min-h-0 flex-1 overflow-hidden md:flex md:flex-col c-card border rounded-xl card-elev">
        <table className="w-full table-fixed text-sm">
          <colgroup><col className="w-[28%]" /><col className="w-[16%]" /><col className="w-[10%]" /><col className="w-[18%]" /><col className="w-[12%]" /><col className="w-[14%]" /><col className="w-[2%]" /></colgroup>
          <thead className="shadow-sm">
            <tr className="c-soft text-left text-[11px] uppercase tracking-[0.14em] c-muted">
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold">Klien</th>
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold">Jenis Akta</th>
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold">No. Akta</th>
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold">Tanggal</th>
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold">Daerah</th>
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold">Di Upload Oleh</th>
              <th className="bg-[#F7F2E8] dark:bg-[#151B22] px-5 py-3.5 font-semibold"></th>
            </tr>
          </thead>
        </table>
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full table-fixed text-sm">
            <colgroup><col className="w-[28%]" /><col className="w-[16%]" /><col className="w-[10%]" /><col className="w-[18%]" /><col className="w-[12%]" /><col className="w-[14%]" /><col className="w-[2%]" /></colgroup>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-14 text-center text-[#A0A0A0]">Memuat...</td></tr>
              ) : docs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-20 text-center">
                    <EmptyState />
                  </td>
                </tr>
              ) : (
                docs.map((d) => (
                  <tr key={d._id} onClick={() => openDetail(d)} data-testid="doc-row" className="border-t c-border hover:bg-[#F2EFE8] dark:hover:bg-white/[0.04] cursor-pointer transition-colors">
                    <td className="px-5 py-4 font-medium c-strong truncate">{d.client_name}</td>
                    <td className="px-5 py-4 c-muted truncate">{d.document_type}</td>
                    <td className="px-5 py-4 font-mono c-muted truncate">{d.deed_number || "-"}</td>
                    <td className="px-5 py-4 font-mono c-muted truncate">{d.explorer_modified_at ? formatExplorerDate(d.explorer_modified_at) : (d.deed_date || "-")}</td>
                    <td className="px-5 py-4 c-muted truncate">{d.region || "-"}</td>
                    <td className="px-5 py-4 c-muted truncate">{d.uploaded_by_name || d.owner_name || "-"}</td>
                    <td className="px-5 py-4 text-gold">{d.storage_path ? <FileText className="w-4 h-4" strokeWidth={1.6} /> : null}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-2 border-t c-border px-4 py-3 text-xs c-muted sm:flex-row sm:items-center sm:justify-between">
          <span>Menampilkan {docs.length} dari {total} dokumen{q ? ` untuk "${q}"` : ""}</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg border c-border px-3 py-1.5 disabled:opacity-40">Sebelumnya</button>
            <span className="font-mono">{page}/{pages}</span>
            <button type="button" disabled={page >= pages || loading} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="rounded-lg border c-border px-3 py-1.5 disabled:opacity-40">Berikutnya</button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto md:hidden space-y-3 pr-1">
        {loading ? (
          <div className="py-14 text-center text-[#A0A0A0]">Memuat...</div>
        ) : docs.length === 0 ? (
          <div className="py-20 text-center"><EmptyState /></div>
        ) : (
          docs.map((d) => (
            <div key={d._id} onClick={() => openDetail(d)} data-testid="doc-row-mobile" className="c-card border rounded-xl p-4 card-elev active:scale-[0.98] transition-all cursor-pointer">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-lg c-soft border c-border flex items-center justify-center text-gold mt-0.5">
                  <FileText className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium c-strong text-sm truncate">{d.client_name}</div>
                  <div className="text-xs c-muted mt-0.5">{d.document_type}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs c-muted">
                    {d.deed_number && <span className="font-mono">#{d.deed_number}</span>}
                    {d.region && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{d.region}</span>}
                    {(d.explorer_modified_at || d.deed_date) && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{d.explorer_modified_at ? formatExplorerDate(d.explorer_modified_at) : d.deed_date}</span>}
                    <span>Di Upload Oleh {d.uploaded_by_name || d.owner_name || "-"}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs c-muted md:hidden">
        <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg border c-border px-3 py-1.5 disabled:opacity-40">Sebelumnya</button>
        <span className="font-mono">{page}/{pages}</span>
        <button type="button" disabled={page >= pages || loading} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="rounded-lg border c-border px-3 py-1.5 disabled:opacity-40">Berikutnya</button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <>
      <div className="w-14 h-14 mx-auto rounded-full c-soft flex items-center justify-center mb-4">
        <FileQuestion className="w-7 h-7 text-[#C9C4B8] dark:text-[#5a626b]" strokeWidth={1.25} />
      </div>
      <div className="text-sm c-muted">Tidak ada dokumen yang cocok.</div>
    </>
  );
}
